import { SourceDataInitials } from '@/constants/SourceDataInitials';
import { GridColumnType } from '@/constants/GridColumnType';
import GridHeaderCellData, { newGridHeaderCellData } from '@/models/grid/GridHeaderCellData';
import GridRowCellData, { newGridRowCellData } from '@/models/grid/GridRowCellData';

import Utils from './QSUtils';
import ICompAddress from '@/models/CompAddress';
import ISourceData from '@/models/comp/ISourceData';
import IFieldData from '@/models/forms/IFieldData';
import ITransferComparisonValue from '@/models/TransferComparisonValue';
import SymbolDetailsUtil from '@/models/common/SymbolDetailsUtil.';
import IFeedData from '@/models/comp/IFeedData';
import utils from './QSUtils';
import { FieldNames } from '@/constants/FieldNames';
import IFormFieldRules from '@/models/form-field/IFormFieldRules';
import IFormFieldRule from '@/models/form-field/IFormFieldRule';
import { formFieldsStoreV36 } from '@/store';

const noCommaFieldNames = new Set(['LATITUDE', 'LONGITUDE', 'DB_ACTAGE', 'PU_YR_BLT', 'YR_BLT', 'PJIYRBUILT', 'DB_ZIP', 'DB_ZIPCODE', 'SUBZIP', 'PU_APN', 'PU_ALT_APN']);
const dateFormatFieldNames = new Set(['TRANSFER_HISTORY_DATE', 'SC_CONTRACT_DATE']);

/**
 * Get the grid column header properties for the given initials.
 * Users may or may not use the SourceDataInitials enum.
 * @param initials
 * @returns
 */
export function getHeaderCellData(initials: string | SourceDataInitials): GridHeaderCellData {
    const symbolDetails = SymbolDetailsUtil.getBySymbol(initials);

    const cellData = newGridHeaderCellData();
    cellData.initials = symbolDetails.symbol;
    cellData.name = symbolDetails.name;
    cellData.title = symbolDetails.title;

    switch (initials.toLowerCase()) {
        case SourceDataInitials.FieldData:
            // The Form field names column
            cellData.type = GridColumnType.FieldData;
            break;
        case SourceDataInitials.SubjectProperty:
            cellData.type = GridColumnType.SubjectProperty;
                cellData.initials = SourceDataInitials.SubjectProperty;
            cellData.name = 'Subject Property';
                break;
        case SourceDataInitials.TransferData:
            // The Transfer values column
            cellData.type = GridColumnType.TransferData;
            break;
        case SourceDataInitials.CascadeData:
            // The column precedence column
            cellData.type = GridColumnType.CascadeData;
            break;
        case SourceDataInitials.Database:
        case SourceDataInitials.SmartExchange:
            cellData.type = GridColumnType.FeedData;
            cellData.subCompIdx = null;
            break;
        case SourceDataInitials.Mls:
        case SourceDataInitials.PublicRecords:
        case SourceDataInitials.CustomValue:
        case SourceDataInitials.UserOverride:
        case SourceDataInitials.FormFiller:
            cellData.type = GridColumnType.FeedData;
            break;
        default:
            cellData.type = GridColumnType.Unknown;
    }
    return cellData;
}

/**
 * Get the grid column cell properties for the given initials.
 * Users may or may not use the SourceDataInitials enum.
 * @param initials
 * @returns
 */
export function getRowCellData(initials: string | SourceDataInitials): GridRowCellData {
    const cellData: GridRowCellData = newGridRowCellData();

    switch (initials.toLowerCase()) {
        case SourceDataInitials.FieldData:
            // The Form field names column
            cellData.type = GridColumnType.FieldData;
            cellData.initials = SourceDataInitials.FieldData;
            break;
            case SourceDataInitials.SubjectProperty:
                cellData.type = GridColumnType.SubjectProperty;
                cellData.initials = SourceDataInitials.SubjectProperty;
                break;
        case SourceDataInitials.TransferData:
            // The Transfer values column
            cellData.type = GridColumnType.TransferData;
            cellData.initials = SourceDataInitials.TransferData;
            break;
        case SourceDataInitials.CascadeData:
            // The column precedence column
            cellData.type = GridColumnType.CascadeData;
            cellData.initials = SourceDataInitials.CascadeData;
            break;
        case SourceDataInitials.Database:
            cellData.type = GridColumnType.FeedData;
            cellData.initials = SourceDataInitials.Database;
            cellData.sourceInitialIdx = undefined;
            break;
        case SourceDataInitials.Mls:
            cellData.type = GridColumnType.FeedData;
            cellData.initials = SourceDataInitials.Mls;
            break;
        case SourceDataInitials.PublicRecords:
            cellData.type = GridColumnType.FeedData;
            cellData.initials = SourceDataInitials.PublicRecords;
            break;
        case SourceDataInitials.SmartExchange:
            cellData.type = GridColumnType.FeedData;
            cellData.initials = SourceDataInitials.SmartExchange;
            cellData.sourceInitialIdx = undefined;
            break;
        case SourceDataInitials.CustomValue:
            cellData.type = GridColumnType.FeedData;
            cellData.initials = SourceDataInitials.CustomValue;
            break;
        case SourceDataInitials.UserOverride:
            cellData.type = GridColumnType.FeedData;
            cellData.initials = SourceDataInitials.UserOverride;
            break;
        case SourceDataInitials.FormFiller:
            cellData.type = GridColumnType.FeedData;
            cellData.initials = SourceDataInitials.FormFiller;
            break;
        default:
            cellData.type = GridColumnType.Unknown;
            cellData.initials = SourceDataInitials.None;
    }
    return cellData;
}

export function parseFlattenedDataPriorities(sourcesString: string) {
    const allPriorities = ['DB', 'SE', 'PR', 'MLS', 'CV'];
    const priorities = cleanDataPrioritiesStringDelimiter(sourcesString);
    return mergeActiveAndAllSources(priorities, allPriorities);
}

function mergeActiveAndAllSources(priorities: any, allpriorities: any) {
    const result = [];
    let i = 1;
    for (const idx in priorities) {
        let customValue = '';
        if (priorities[idx].includes('CV')) {
            customValue = getCVValueFromDataPrioritiesStringDelimiter(priorities[idx]);
            priorities[idx] = 'CV';
        }

        const details = Utils.initialDetails(priorities[idx]);
        const tmp = {
            id: details['id'] !== '' ? Number(details['id']) : details['id'],
            name: details['title'],
            initials: details['initials'],
            order: i,
            isHidden: false,
            customValue: priorities[idx] === 'CV' ? customValue : '',
        };
        result.push(tmp);
        i++;
    }

    for (const idx in allpriorities) {
        const itemIdx = result.findIndex((x) => x.initials === allpriorities[idx]);
        if (itemIdx === -1) {
            const details = Utils.initialDetails(allpriorities[idx]);
            const tmp = {
                id: details['id'] !== '' ? Number(details['id']) : details['id'],
                name: details['name'],
                initials: details['initials'],
                order: i,
                isHidden: true,
                customValue: '',
            };
            result.push(tmp);
            i++;
        }
    }

    return result;
}

export function cleanDataPrioritiesStringDelimiter(sourcesString: string) {
    let list;
    const value = sourcesString.replace(/\|\s*$/, ''); // remove trailing "|"
    if (value.indexOf(' | ') > -1) {
        list = value.split(' | ').map(function (item) {
            item = item.replace('""', '');
            item = item.trim();
            return item;
        });
    } else {
        // Value will have a "_".  We this just to put the value into array
        list = value.split('_').map(function (item) {
            item = item.replace('""', '');
            item = item.trim();
            return item;
        });
    }

    // Filter out blanks
    list = list.filter(function (item) {
        return item !== '';
    });

    return list;
}

function getCVValueFromDataPrioritiesStringDelimiter(priorityValue: string) {
    let value = '';
    let list: any = [];
    if (priorityValue.includes('CV')) {
        list = priorityValue.split('"').map(function (item: string) {
            item = item.replace('"', '');
            item = item.trim();
            return item;
        });
    }

    if (list.length > 0 && Utils.isKeyInObject(list, 1)) {
        value = list[1];
    }

    return value;
}

export function styleFlattenedDataPriorities(sourcesString: string) {
    const result = parseFlattenedDataPriorities(sourcesString);

    let value = '';
    const icon = `<i class="far fa-edit p-color-1"></i>`;
    if (result.length > 0) {
        let count = 1;
        for (const idx in result) {
            let labal = result[idx].initials;

            if (result[idx].initials === 'CV' && result[idx].customValue === '') {
                continue;
            }
            if (result[idx].isHidden) {
                continue;
            }

            // Preappend Icon
            if (count === 1) {
                value = icon;
            }

            if (result[idx].initials === 'CV' && result[idx].customValue !== '') {
                labal = `${result[idx].initials} "${result[idx].customValue}"`;
            }

            const initial = result[idx].initials.toLowerCase();
            value = `${value}  ${count}.<span class="ms-1 fw-bold initial-text-${initial}">${labal}</span>`;
            count++;
        }
    }

    if (value === '') {
        value = `${icon} Set Custom Data Preference`;
    }

    return value;
}

export function formatData(value: any, fieldName: string) {
    const field = getNormalizedFieldName(fieldName);
    const normalizedFieldName = field.baseFieldname;

    // No formatting on BL_BLANK fields
    if (!normalizedFieldName.includes('BL_BLANK')) {

        if (dateFormatFieldNames.has(normalizedFieldName)) {
            // Format if date
            value = Utils.formatValueToDate(value);
        } else if (!noCommaFieldNames.has(normalizedFieldName)) {
            // Format if number
            value = Utils.formatNumberToCommas(value);
        } 
    }

    return value;
}

export function getNormalizedFieldName(fieldName: string) {
    if (!fieldName || fieldName.indexOf('-') === -1) {
        return {fieldnameIndex: "", baseFieldname: fieldName};
    }

    // ie.  Dwellings-0-Heating-0-DWELL_HEAT
    //      fieldnameIndex = Dwellings-0-Heating-0
    //      baseFieldname = DWELL_HEAT
    const lastIndex = fieldName.lastIndexOf('-');
    if (lastIndex > -1) {
        const fieldnameIndex = fieldName.slice(0, lastIndex);
        const baseFieldname = fieldName.slice(lastIndex + 1);
        return {fieldnameIndex: fieldnameIndex, baseFieldname: baseFieldname};
    }
    return {fieldnameIndex: "", baseFieldname: fieldName};
}

export function transferValueCasing(value: any, casing: string) {
    let val = value;
    switch (casing.toLowerCase()) {
        case 'upper':
            val = Utils.toUpperCase(val);
            break;
        case 'lower':
            val = Utils.toLowerCase(val);
            break;
        case 'sentence':
            val = Utils.toSentenceCase(val);
            break;
        case 'title':
            val = Utils.toTitleCase(val);
            break;
        default:
            break;
    }

    return val;
}

export function buildAddress2(address: ICompAddress) {
    let result = '';
    const city = Utils.isKeyInObject(address, 'city') && address.city ? address.city : '';
    const state = Utils.isKeyInObject(address, 'state') && address.state ? address.state : '';
    const zipCode = Utils.isKeyInObject(address, 'zipCode') && address.zipCode ? address.zipCode : '';

    if (city && state) {
        result = `${city}, ${state}`;
    }

    result = `${result} ${zipCode}`;

    return result;
}

export function getUseAllDataValues(sourceData: ISourceData, fieldData: IFieldData[]): IFormFieldRules {
    const fieldRules: IFormFieldRules = [];

    console.log("getUseAllDataValues");
    console.log("sourceData", sourceData);
    console.log("fieldData", fieldData);

    const sourceDataFilter = Object.keys(sourceData)
        .filter((k) => sourceData[k] !== '' && sourceData[k] !== null)
        .reduce((a, k) => ({ ...a, [k]: sourceData[k] }), {});

    const flattenedSourceData: Record<string, any> = {};
    const appendSourceData = (value: any, path: string): void => {
        if (Array.isArray(value)) {
            value.forEach((item, index) => appendSourceData(item, path ? `${path}-${index}` : String(index)));
        } else if (value && typeof value === 'object') {
            for (const [key, childValue] of Object.entries(value)) {
                appendSourceData(childValue, path ? `${path}-${key}` : key);
            }
        } else if (value !== '' && value !== null && value !== undefined) {
            flattenedSourceData[path] = value;
        }
    };
    appendSourceData(sourceData, '');

    console.log("sourceDataFilter", sourceDataFilter);

    for (const fieldDataItem of fieldData) {
        const fieldName = fieldDataItem.fieldName;

        // Direct base field match
        if (Object.prototype.hasOwnProperty.call(sourceDataFilter, fieldName)) {
            const fieldRule: IFormFieldRule = {
                fieldId: fieldDataItem.fieldId,
                // @ts-ignore
                value: sourceDataFilter[fieldName],
            };
            fieldRules.push(fieldRule);
        }

        // Repeatable field matches (e.g. FIELDNAME-0, FIELDNAME-1)
        const repeatableFieldPattern = new RegExp(`^${fieldName}-(\\d+)$`);
        for (const sourceDataField of Object.keys(sourceDataFilter)) {
            const match = sourceDataField.match(repeatableFieldPattern);
            if (!match) {
                continue;
            }

            const repeatIndex = Number(match[1]);
            const repeatFieldId = repeatIndex === 0 ? fieldDataItem.fieldId : Number(`${fieldDataItem.fieldId}.${repeatIndex}`);

            const fieldRule: IFormFieldRule = {
                fieldId: repeatFieldId,
                // @ts-ignore
                value: sourceDataFilter[sourceDataField],
            };

            fieldRules.push(fieldRule);
        }

        // Collection-wrapped subject fields, such as
        // ProjectType-0-PROJECT_TYPE_FLATTENED, use the base field ID for index 0.
        for (const [sourceDataField, value] of Object.entries(flattenedSourceData)) {
            if (!sourceDataField.endsWith(`-${fieldName}`)) {
                continue;
            }

            const prefix = sourceDataField.slice(0, -(fieldName.length + 1));
            const numericIndices = prefix.split('-').filter((part) => /^\d+$/.test(part)).map(Number);
            const indices = numericIndices.length > 0 ? numericIndices : [0];
            const repeatFieldId = indices.every((index) => index === 0)
                ? fieldDataItem.fieldId
                : Number(`${fieldDataItem.fieldId}.${indices.join('')}`);
            if (!fieldRules.some((rule) => rule.fieldId === repeatFieldId)) {
                fieldRules.push({
                    fieldId: repeatFieldId,
                    value: String(value),
                });
            }
        }
    }

    console.log("fieldRules", fieldRules);

    return fieldRules;
}

/**
 * When MLS data exists display the MLS import name.
 * @param cellData
 * @param feedData
 */
export function updateMlsHeaderName(cellData: GridHeaderCellData, feedData: IFeedData) {
    if (feedData && feedData.sourceData) {
        if (utils.isKeyInObject(feedData.sourceData, FieldNames.MlsImportName)) {
            cellData.name = feedData.sourceData[FieldNames.MlsImportName];
        }
    }
}

export function transferValueComparison(suggestFieldRule: boolean, originalValue: string, newValue: string) {
    // QSRC-1047
    let result: ITransferComparisonValue = {};
    const delimiters = [',', ';', ':', '/', '\\'];
    let diffDelimitersOriginalFound = 0;
    let diffDelimitersNewFound = 0;
    const delimiterResult = [];
    let formatted = '';
    let changes = [];

    // Only run the following code if suggestFieldRule is true
    if (!suggestFieldRule) {
        return result;
    }

    // If either value is blank then return
    if (originalValue === '' || newValue === '') {
        return result;
    }

    // If either value length is less then 2 then return
    if (originalValue.length <= 1 || newValue.length <= 1) {
        return result;
    }

    // Check how many different (unique) delimiters exists
    for (const idx in delimiters) {
        if (originalValue.indexOf(delimiters[idx]) !== -1) {
            delimiterResult.push({
                delimiter: delimiters[idx],
                parsed: originalValue.split(delimiters[idx]),
            });
            diffDelimitersOriginalFound++;
        }

        if (newValue.indexOf(delimiters[idx]) !== -1) {
            delimiterResult.push({
                delimiter: delimiters[idx],
                parsed: newValue.split(delimiters[idx]),
            });
            diffDelimitersNewFound++;
        }
    }

    // If more then one (unique) delimiter was found then return
    if (diffDelimitersOriginalFound >= 2 || diffDelimitersNewFound >= 2) {
        return result;
    }

    // If Original or New value has a delimiter and the other does not then return
    if ((diffDelimitersOriginalFound >= 1 && diffDelimitersNewFound === 0) || (diffDelimitersOriginalFound === 0 && diffDelimitersNewFound >= 1)) {
        return result;
    }

    // Has delimiters
    if (diffDelimitersOriginalFound === 1 && diffDelimitersNewFound === 1) {
        // If parsed results do not match in size then return
        if (delimiterResult[0]['parsed'].length !== delimiterResult[1]['parsed'].length) {
            return result;
        }

        changes = transferValueComparisonGetChanges(delimiterResult);

        // If multiple changes detected then return
        if (changes.length > 1) {
            return result;
        }

        // Build formattedText
        formatted = transferValueComparisonFormattedText(delimiterResult);

        // No delimiter
    } else {
        if (originalValue !== newValue) {
            changes.push({
                before: originalValue,
                after: newValue,
            });

            // Build formattedText
            const diff = getDifference(originalValue, newValue);
            if (diff !== '') {
                formatted = newValue.replace(diff, `<b>${diff}</b>`);
            }
        }
    }

    result = {
        fullText: {
            originalValue: originalValue,
            newValue: newValue,
        },
        changedText: {
            originalValue: changes[0]['before'],
            newValue: changes[0]['after'],
        },
        formattedText: formatted,
    };

    return result;
}

export function getParentChildFieldsList() {

    // const fields: Record<string, string[]> = {
    //     YR_BLT: ['PU_YR_BLT', 'PU_YR_BLT_EST'],
    //     SC_SOURCE1: ['PU_DATA_SOURCE', 'PU_DATA_SOURCE_ASSESSOR_ID', 'PU_DATA_SOURCE_MLS_NUM'],
    //     AMENITY_MISC: ['PU_AMENITY_MISC', 'PU_AMENITY_MISC_NUM'],
    //     AMENITY_OUTDOOR_ACCESSORIES: ['PU_AMENITY_OUTDOOR_ACCESSORIES', 'PU_AMENITY_OUTDOOR_ACCESSORIES_NUM'],
    //     AMENITY_WATER_FEATURES: ['PU_AMENITY_WATER_FEATURE', 'PU_AMENITY_WATER_FEATURE_DETAIL', 'PU_AMENITY_WATER_FEATURE_NUM'],
    //     AMENITY_WHOLE_HOME: ['PU_AMENITY_WHOLE_HOME', 'PU_AMENITY_WHOLE_HOME_NUM'],
    //     AMENITY_OUTDOOR_LIVING: ['PU_AMENITY_OUTDOOR_LIVING'],
    //     GREEN_BUILD_CERT: ['PU_GREEN_BUILD_CERT_DESC', 'PU_GREEN_BUILD_CERT_RATE'],
    //     GREEN_EFF_RATING: ['PU_GREEN_EFF_RATING', 'PU_GREEN_EFF_SCORE'],
    //     SITE_STREET_TYPE: ['PU_SITE_STREET_TYPE', 'PU_SITE_STREET_SURFACE'],
    //     ST_VIEW: ['PU_ST_VIEW', 'PU_ST_RANGE'],
    //     VEHICLE_STORAGE: ['PU_VEHICLE_STORAGE', 'PU_VEHICLE_STORAGE_SPACES', 'PU_VEHICLE_STORAGE_DETAILS', 'PU_VEHICLE_STORAGE_SQFT'],
    //     WATER_FRONTAGE: ['PU_WATER_FRONTAGE_TYPE', 'PU_WATER_FRONTAGE_NAME', 'PU_WATER_FRONTAGE_ACCESS_DEPTH'],
    //     BATHS: ['PU_FULL_BATHS', 'PU_HALF_BATHS'],
    //     LOC_ADU: ['PU_LOC_ADU_TYPE', 'PU_LOC_ADU_DESC'],
    //     DWELL_DISASTER_MIT: ['PU_DWELL_DISASTER_MIT'],
    //     DWELL_FUNC_ISSUES: ['PU_DWELL_FUNC_ISSUES'],
    //     DWELL_STYLE: ['PU_DWELL_STYLE'],
    //     DWELL_MANFCT_HOME_WIDTH: ['PU_DWELL_MANFCT_HOME_WIDTH'],
    //     DWELL_CONST_METHOD: ['PU_DWELL_CONST_METHOD'],
    //     DWELL_TH_LOC: ['PU_DWELL_TH_LOC'],
    //     DWELL_TH_BACKTOBACK: ['PU_DWELL_TH_BACKTOBACK'],
    //     DWELL_DESIGN: ['PU_STRUCTURE_DESIGN'],
    //     DWELL_HEAT: ['PU_DWELL_HEAT_SYSTEM', 'PU_DWELL_HEAT_DETAIL'],
    //     EXT_COND_ROOF: ['PU_EXT_COND_ROOF_OBSERVABLE', 'PU_EXT_COND_ROOF_STATUS'],
    //     SITE_INFLUENCE: ['PU_SITE_INFLUENCE', 'PU_SITE_INFLUENCE_PRIVATE_ACCESS', 'PU_SITE_INFLUENCE_WATER_TYPE'],
    //     PROJ_NAME: ['PU_PROJECT_NAME', 'PU_SAME_PROJECT_AS_SUBJECT'],
    //     HEAT_SYSTEM: ['HEAT_SYSTEM', 'HEAT_DETAIL'],
    //     MECH_HEAT: ['MECH_HEAT_SYSTEM', 'MECH_HEAT_DETAIL'],
    //     LEVEL_IN_UNIT: ['LEVEL_IN_UNIT', 'LEVEL_GRADE_TYPE', 'LEVEL_BELOW_GRADE_ACCESS', 'LEVEL_BELOW_GRADE_EXTERIOR_ACCESS', 'LEVEL_FINISHED_AREA', 'LEVEL_UNFINISHED_AREA'],
    //     ROOM_LEVEL: ['ROOM_LEVEL', 'ROOM_ROOM_TYPE'],
    //     FINISHED_AREA_TOTAL_AREA: ['FINISHED_AREA_TOTAL_AREA', 'FINISHED_AREA_ROOM_SUMMARY_TOTAL_ROOM', 'FINISHED_AREA_ROOM_SUMMARY_ROOM_TYPE'],
    //     DEFECT_FEATURE: ['DEFECT_FEATURE', 'DEFECT_LOCATION', 'DEFECT_DESC', 'DEFECT_INTEGRITY', 'DEFECT_RECOMMEND_ACTION'],
    //     EXT_WINDOW_DETAIL: ['EXT_WINDOW_DETAIL', 'EXT_WINDOW_STATUS'],
    //     EXTRFSURFACE: ['EXTRFSURFACE_TYPE', 'EXTRFSURFACE_OBSERVABLE', 'EXTRFSURFACE_AGE'],
    //     SIT_STREET_TYPE_SURFACE: ['PU_STREET_TYPE', 'PU_SURFACE_TYPE'],
    //     SIT_INFL: ['SIT_INFL_INFL', 'PU_SITE_INFL_WTR_DTL_TYPE', 'PU_SITE_INFL_WTR_DTL_PRIV_ACCESS'],
    //     SIT_VWIMP_VIEW: ['SIT_VWIMP_VIEW', 'SIT_VWIMP_RNGVW', 'SIT_VWIMP_IMP'],
    //     EE_CERTS_ORGANIZATION: ['EE_CERTS_ORGANIZATION', 'EE_CERTS', 'EE_YEAR', 'EE_VERSION', 'EE_RATING'],
    //     EE_GREEN_EFF: ['EE_GREEN_EFF', 'EE_GREEN_RATING', 'EE_GREEN_SCORE'],
    //     EE_RENEWABLES: ['PU_EE_RENEWABLE_TYPE', 'PU_EE_OWNER_TYPE', 'PU_EE_FINANCING_ARRANGEMENT_YES'],
    //     SIT_FEAT_ENCROACH_DTL: ['PU_SITE_FEAT_ENCROACH', 'PU_SITE_FEAT_ENCROACH_BRD_ON_SUB', 'PU_SITE_FEAT_ENCROACH_BRD_ON_ADJ'],
    //     SIT_FEAT_SITCHAR_DTL: ['PU_SITE_FEAT_SITCHAR', 'PU_SITE_FEAT_SITCHAR_TOP_TYPE', 'PU_SITE_FEAT_SITCHAR_DRAIN_RSN'],
    //     VS_CAR_STORAGE: ['VS_PU_CAR_STORAGE_TYPE', 'VS_PU_STORAGE_NUMBER_SPACES', 'VS_PU_STORAGE_TYPE_DETAIL', 'VS_PU_STORAGE_SPACE_SQUARE_FEET', 'VS_PU_DRIVEWAY_SURFACE_MATERIAL', 'VS_PU_PARKING_SPACE_ASSIGNMENT'],
    //     EXT_FOUNDATION_DETAIL: ['EXT_FOUNDATION_MATERIAL_DETAIL', 'EXT_FOUNDATION_TYPE_DETAIL'],
    //     SPA_AMENITY_CATEGORY: ['PU_SPA_AMENITY_TYPE', 'PU_SPA_AMENITY_CATEGORY', 'PU_SPA_AMENITY_MATERIAL', 'PU_SPA_AMENITY_TOTALNUM', 'PU_SPA_AMENITY_DETAIL_AREA', 'PU_SPA_AMENITY_DETAIL_FEATURE'],
    //     NATIVE_AM_LANDS: ['PU_NATIVE_AM_LANDS_YES', 'PU_NATIVE_AM_LANDS_NO', 'PU_NATIVE_AM_LANDS_TYPE'],
    //     CONT_DATE: ['PU_CONT_DATE_KNOWN', 'PU_CONT_DATE'],
    //     EX_CONCAMT: ['PU_CONC_PRESENT', 'PU_CONC_AMOUNT'],
    //     CONT_PRICE: ['PU_CONT_PRICE_KNOWN', 'PU_CONT_PRICE'],
    //     PROX1: ['PU_DISTANCE', 'PU_MILES', 'PU_KILOMETERS', 'PU_DIRECTION'],
    //     PI_AMENITIES_SERVICES_INCLUDED: ['PU_AMENITIES_SERVICES_INCLUDED'],
    //     APN: ['PU_APN', 'PU_ALT_APN', 'PU_APN_DESC', 'PU_SOURCE_APN']
    // };

    const fields: Record<string, string[]> = formFieldsStoreV36.ParentChildFields ?? {};
    return fields;
}

export function getParentChildFields(fieldName: string): string[] {
    const normalizedFieldName = getNormalizedFieldName(fieldName).baseFieldname.replace(/_FLATTENED$/, '');
    return getParentChildFieldsList()[normalizedFieldName] || [];
}

function getDifference(string1: string, string2: string) {
    let i = 0;
    let j = 0;
    let result = '';

    while (j < string2.length) {
        if ((typeof string1[i] === 'string' && typeof string2[j] === 'string' && Utils.toLowerCase(string1[i]) !== Utils.toLowerCase(string2[j])) || i === string1.length) result += string2[j];
        else i++;
        j++;
    }
    return result;
}

function transferValueComparisonGetChanges(delimiterResult: any) {
    const result = [];
    for (let i = 0; i < delimiterResult[0]['parsed'].length; i++) {
        const originalValue = delimiterResult[0]['parsed'][i];
        const newValue = delimiterResult[1]['parsed'][i];

        if (originalValue.toLowerCase() !== newValue.toLowerCase()) {
            result.push({
                before: originalValue,
                after: newValue,
            });
        }
    }

    return result;
}

function transferValueComparisonFormattedText(delimiterResult: any) {
    let result = '';
    for (let i = 0; i < delimiterResult[0]['parsed'].length; i++) {
        const originalValue = delimiterResult[0]['parsed'][i];
        const newValue = delimiterResult[1]['parsed'][i];

        if (originalValue.toLowerCase() !== newValue.toLowerCase()) {
            const diff = getDifference(originalValue, newValue);
            if (diff !== '') {
                result += newValue.replace(diff, `<b>${diff}</b>`);
            }
        } else {
            result += newValue;
        }

        if (i < delimiterResult[0]['parsed'].length - 1) {
            result += delimiterResult[1]['delimiter'];
        }
    }

    return result;
}

const GridColumnUtils = {
    getHeaderCellData,
    getRowCellData,
    cleanDataPrioritiesStringDelimiter,
    parseFlattenedDataPriorities,
    styleFlattenedDataPriorities,
    formatData,
    getNormalizedFieldName,
    getParentChildFieldsList,
    getParentChildFields,
    transferValueCasing,
    buildAddress2,
    getUseAllDataValues,
    updateMlsHeaderName,
    transferValueComparison,
};
export default GridColumnUtils;
